'use client';

import { MessageCircle, Reply } from 'lucide-react';
import { CondolenceComment } from '@/lib/types';
import { formatDate, getInitials } from '@/lib/utils';

interface CondolencesProps {
  condolences: CondolenceComment[];
}

export function Condolences({ condolences }: CondolencesProps) {
  const publicCondolences = condolences.filter(c => c.isPublic);

  if (publicCondolences.length === 0) {
    return (
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-center gap-3 mb-8">
            <MessageCircle className="w-6 h-6 text-memorial-secondary" />
            <h2 className="font-serif text-2xl md:text-3xl text-memorial-primary">
              Kondolenzen
            </h2>
          </div>
          <p className="text-center text-memorial-text-secondary">
            Noch keine Kondolenzen vorhanden. Seien Sie der Erste, der sein Beileid ausspricht.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 bg-white">
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-8">
          <MessageCircle className="w-6 h-6 text-memorial-secondary" />
          <h2 className="font-serif text-2xl md:text-3xl text-memorial-primary">
            Kondolenzen
          </h2>
        </div>
        <p className="text-center text-memorial-text-secondary mb-8">
          {publicCondolences.length} {publicCondolences.length === 1 ? 'Nachricht' : 'Nachrichten'} des Beileids
        </p>

        <div className="space-y-6">
          {publicCondolences.map((condolence) => (
            <div
              key={condolence.id}
              className="bg-memorial-background rounded-xl p-6 shadow-sm border border-memorial-muted"
            >
              {/* Header */}
              <div className="flex items-start gap-4 mb-4">
                {/* Avatar */}
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-memorial-secondary/20 flex items-center justify-center text-memorial-secondary font-medium">
                  {getInitials(condolence.firstName, condolence.lastName)}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-memorial-text-primary">
                    {condolence.firstName} {condolence.lastName}
                  </h3>
                  {condolence.createdAt && (
                    <p className="text-sm text-memorial-text-secondary">
                      {formatDate(condolence.createdAt, 'd. MMMM yyyy')}
                    </p>
                  )}
                </div>
              </div>

              {/* Message */}
              <div className="text-memorial-text-primary leading-relaxed whitespace-pre-wrap">
                {condolence.descriptions}
              </div>

              {/* Replies */}
              {condolence.replyToCondolenceComments && condolence.replyToCondolenceComments.length > 0 && (
                <div className="mt-4 pt-4 border-t border-memorial-muted space-y-4">
                  {condolence.replyToCondolenceComments.map((reply) => (
                    <div key={reply.id} className="ml-4 pl-4 border-l-2 border-memorial-secondary/30">
                      <div className="flex items-center gap-2 mb-2">
                        <Reply className="w-4 h-4 text-memorial-secondary" />
                        <span className="font-medium text-sm text-memorial-text-primary">
                          {reply.account?.firstName} {reply.account?.lastName}
                        </span>
                        <span className="text-memorial-secondary text-sm">
                          (Familie)
                        </span>
                        {reply.createdAt && (
                          <span className="text-xs text-memorial-text-secondary">
                            · {formatDate(reply.createdAt, 'd. MMMM yyyy')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-memorial-text-primary/80">
                        {reply.description}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
